import { useEffect } from "react"
import { navigate } from "gatsby"
import { useTranslation } from "react-i18next"
import { defaultLanguage, languageCodes } from "./config"
import { setCookie } from "@filmustage/shared"

const COOKIE_KEY = "i18nextLng"
const DETECTED_KEY = "i18n-detected"

// Map browser language codes (ISO 639-1) to our URL locale codes
const browserToLocale = { "pt-br": "br", be: "by", uk: "ua" }

/**
 * On first visit (no localStorage), detect browser language
 * and redirect to the matching locale if available.
 * Only runs once - sets a flag in localStorage after detection.
 */
export function useLanguageRedirect() {
  const { i18n } = useTranslation()

  useEffect(() => {
    if (typeof window === "undefined") return

    // Only run detection once ever
    if (localStorage.getItem(DETECTED_KEY)) return

    // Only redirect from EN pages (no lang prefix in URL)
    const pathname = window.location.pathname
    const firstSegment = pathname.split("/").filter(Boolean)[0]
    if (firstSegment && languageCodes.includes(firstSegment)) {
      localStorage.setItem(DETECTED_KEY, "1")
      return
    }

    // Mark detection as done regardless of outcome
    localStorage.setItem(DETECTED_KEY, "1")

    // Detect browser language
    const browserLang = navigator.language || ""
    const browserLangShort = browserLang.split("-")[0]
    const browserLangLower = browserLang.toLowerCase()

    let matchedLang = null
    if (browserToLocale[browserLangLower]) {
      matchedLang = browserToLocale[browserLangLower]
    } else if (browserToLocale[browserLangShort]) {
      matchedLang = browserToLocale[browserLangShort]
    } else if (languageCodes.includes(browserLangShort) && browserLangShort !== defaultLanguage) {
      matchedLang = browserLangShort
    }

    if (matchedLang) {
      setCookie(COOKIE_KEY, matchedLang)
      i18n.changeLanguage(matchedLang)
      navigate(`/${matchedLang}${pathname}`, { replace: true })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
}
